/**
 * features/hr/PayrollWorkspace.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — the Payroll Workspace.
 *
 * The bursar picks a cohort (category + search) and steps through each
 * employee one after another in a clearly separated EMPLOYEE NAVIGATOR,
 * editing the payroll profile (basic, allowances, gratuity, deductions)
 * via the same autosaving editor used on the staff profile.
 *
 * In-workspace payslip generation: a collapsible "Generate payslip"
 * panel produces the current employee's payslip without leaving the
 * workspace. On the final month of a contract it auto-opens with the
 * gratuity payout calculated, and surfaces any OUTSTANDING LOAN BALANCES
 * to recover in full from the final pay (a settlement deduction). The
 * Payslips tab on the individual profile is retained for the rarer
 * cases (e.g. an off-cycle final settlement on death-in-service).
 * ------------------------------------------------------------------ */
import { useMemo, useState, useEffect } from 'react';
import {
  Users, ChevronLeft, ChevronRight, Search, AlertTriangle, ExternalLink,
  Award, Copy, Receipt, Download, Loader2, CheckCircle2, ChevronDown, Wallet,
} from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { SoftCard, StatChip, AccentButton, glassFieldClass } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { useHrData } from '@/features/hr/useHrData';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import { usePayeOverride, savePayeOverride, clearPayeOverride } from '@/features/hr/usePayrollPeriods';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildPayslipPdf } from '@/lib/payslipPdf';
import PayrollTab from '@/features/hr/DeductionsManager';
import {
  staffFullName, STAFF_CATEGORY_LABELS,
  type StaffCategory, type HrStaff, type HrLeaveRequest,
} from '@/lib/hr';
import {
  MONTH_LABELS, fmtPula, round2, computeGratuityTax, contractGratuityBasePaid,
  gratuityAccruedOn, deductionForPeriod, DEDUCTION_TYPES, resolveExitBenefit,
  type StaffAllowance, type HrPayrollProfile, type HrDeduction, type HrTaxTable,
  type HrLeaveAllocation, type HrPayslip, type HrPayrollSettings, type HrGratuityPayout,
} from '@/lib/hrPayroll';
import {
  assemblePayslip, payslipId, monthPeriod, periodLabel, type AssembledPayslip,
} from '@/lib/assemblePayslip';

type CategoryFilter = 'all' | StaffCategory;

function mergeAllowances(existing: StaffAllowance[], incoming: StaffAllowance[]): StaffAllowance[] {
  const map = new Map(existing.map((a) => [a.type.toLowerCase(), a]));
  for (const a of incoming) map.set(a.type.toLowerCase(), { type: a.type, amount: a.amount });
  return Array.from(map.values());
}

export default function PayrollWorkspace({ onOpenStaff }: { onOpenStaff?: (id: string) => void }) {
  const data = useHrData();
  const { userRoles } = useAuth();
  const canEdit = getPermissions(userRoles).canAccessHR;
  const now = new Date();
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [search, setSearch] = useState('');
  const [index, setIndex] = useState(0);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [applyScope, setApplyScope] = useState<'all' | StaffCategory>('all');
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  const cohort = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.staff
      .filter((s) => s.status !== 'exited')
      .filter((s) => category === 'all' || s.category === category)
      .filter((s) => !q || staffFullName(s).toLowerCase().includes(q) || s.employeeNo.toLowerCase().includes(q))
      .sort((a, b) => staffFullName(a).localeCompare(staffFullName(b)));
  }, [data.staff, category, search]);

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading payroll…" /></div>;
  }

  const safeIndex = cohort.length ? Math.min(index, cohort.length - 1) : 0;
  const current = cohort[safeIndex];
  const configuredCount = data.payrollProfiles.filter((p) => cohort.some((s) => s.id === p.staffId) && (p.basicSalary ?? 0) > 0).length;

  function reset(setter: () => void) { setter(); setIndex(0); }

  async function applyAllowances() {
    if (!current) return;
    const src = data.payrollProfiles.find((p) => p.staffId === current.id);
    const srcAllow = src?.allowances ?? [];
    if (srcAllow.length === 0) {
      setSaveModal({ show: true, status: 'error', message: 'Set and save at least one allowance on this employee first.' });
      return;
    }
    const targets = data.staff.filter((s) =>
      s.status !== 'exited' && s.id !== current.id && (applyScope === 'all' || s.category === applyScope));
    setSaveModal({ show: true, status: 'saving', message: 'Applying allowances…' });
    let updated = 0, skipped = 0;
    try {
      for (const s of targets) {
        const tp = data.payrollProfiles.find((p) => p.staffId === s.id);
        if (!tp) { skipped += 1; continue; }
        await hr.payrollProfile.update(s.id, { allowances: mergeAllowances(tp.allowances ?? [], srcAllow) });
        updated += 1;
      }
      setSaveModal({
        show: true, status: 'success',
        message: `Allowances applied to ${updated} staff${skipped ? ` · ${skipped} skipped (no payroll profile yet)` : ''}.`,
      });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    }
  }

  return (
    <div className="space-y-5">
      {/* ---- Header ---- */}
      <div className="flex items-center gap-2.5">
        <div className="grid place-items-center h-9 w-9 rounded-xl bg-[var(--accent-strong,#1b3a6b)]/10 text-[var(--accent-strong,#1b3a6b)] dark:text-[var(--accent-link-dark,#93c5fd)]">
          <Wallet size={18} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Payroll Workspace</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Configure each employee and generate their payslip — without leaving this screen.</p>
        </div>
      </div>

      {/* ---- Cohort toolbar (two compact rows) ---- */}
      <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 px-3 py-2.5 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <select value={category} onChange={(e) => reset(() => setCategory(e.target.value as CategoryFilter))} className={glassFieldClass('py-2 text-sm w-full')}>
            <option value="all">All categories</option>
            {(Object.keys(STAFF_CATEGORY_LABELS) as StaffCategory[]).map((c) => (
              <option key={c} value={c}>{STAFF_CATEGORY_LABELS[c]}</option>
            ))}
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => reset(() => setSearch(e.target.value))} placeholder="Search name or #" className={glassFieldClass('py-2 pl-8 text-sm w-full')} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={glassFieldClass('py-2 text-sm w-full')} aria-label="Run month">
            {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={glassFieldClass('py-2 text-sm w-full')} aria-label="Run year" />
        </div>
      </div>

      {cohort.length === 0 ? (
        <SoftCard hover={false}>
          <p className="text-sm text-gray-500 dark:text-gray-400">No staff match this cohort.</p>
        </SoftCard>
      ) : (
        <div className="space-y-4">
          {/* ===== EMPLOYEE NAVIGATOR — bold contrasting band ===== */}
          <div
            className="rounded-none overflow-hidden shadow-md ring-1 ring-black/5"
            style={{ background: 'linear-gradient(135deg, #3461a3, #5b91d4)' }}
          >
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-white/80 inline-flex items-center gap-1.5">
                <Users size={13} /> Employee {safeIndex + 1} of {cohort.length}
              </span>
              <span className="text-[11px] font-medium text-white/90 inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" /> {configuredCount} configured
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 pb-3">
              <button
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                disabled={safeIndex === 0}
                className="flex items-center gap-1 px-3 py-3 text-sm font-medium rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/25 disabled:opacity-30 transition-colors"
                aria-label="Previous employee"
              >
                <ChevronLeft size={18} /><span className="hidden sm:inline">Prev</span>
              </button>
              <select
                value={safeIndex}
                onChange={(e) => setIndex(Number(e.target.value))}
                className="flex-1 min-w-[200px] py-3 px-3 rounded-xl bg-white text-gray-900 font-semibold text-center shadow-sm border-0 focus:outline-none focus:ring-2 focus:ring-white/70 cursor-pointer"
              >
                {cohort.map((s, i) => {
                  const configured = (data.payrollProfiles.find((p) => p.staffId === s.id)?.basicSalary ?? 0) > 0;
                  return (
                    <option key={s.id} value={i}>
                      {configured ? '✓' : '○'} {staffFullName(s)} · #{s.employeeNo}
                    </option>
                  );
                })}
              </select>
              <button
                onClick={() => setIndex((i) => Math.min(cohort.length - 1, i + 1))}
                disabled={safeIndex === cohort.length - 1}
                className="flex items-center gap-1 px-3 py-3 text-sm font-medium rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/25 disabled:opacity-30 transition-colors"
                aria-label="Next employee"
              >
                <span className="hidden sm:inline">Next</span><ChevronRight size={18} />
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-black/15">
              <span className="text-sm font-semibold text-white">
                {staffFullName(current)}
                <span className="ml-2 text-xs font-normal text-white/70">
                  {STAFF_CATEGORY_LABELS[current.category]} · {current.jobTitle}
                </span>
              </span>
              {onOpenStaff && (
                <button onClick={() => onOpenStaff(current.id)} className="inline-flex items-center gap-1 text-xs text-white/90 hover:text-white hover:underline shrink-0">
                  <ExternalLink size={12} /> full profile
                </button>
              )}
            </div>
          </div>

          {/* ===== In-workspace payslip generation (collapsible) ===== */}
          <GeneratePanel
            key={`${current.id}-${year}-${month}`}
            staff={current}
            profile={data.payrollProfiles.find((p) => p.staffId === current.id)}
            deductions={data.deductions.filter((d) => d.staffId === current.id)}
            contracts={data.contracts.filter((c) => c.staffId === current.id)}
            payslips={data.payslips.filter((p) => p.staffId === current.id)}
            gratuityPayouts={data.gratuityPayouts.filter((p) => p.staffId === current.id)}
            leaveAllocation={data.leaveAllocations.find((a) => a.staffId === current.id)}
            leave={data.leave.filter((l) => l.staffId === current.id)}
            activeTaxTable={data.activeTaxTable}
            settings={data.payrollSettings}
            year={year}
            month={month}
            canEdit={canEdit}
          />

          {/* ===== Payroll editor (autosaves) ===== */}
          <PayrollTab
            key={current.id}
            staff={current}
            profile={data.payrollProfiles.find((p) => p.staffId === current.id)}
            deductions={data.deductions.filter((d) => d.staffId === current.id)}
            allowanceCatalog={data.allowanceCatalog}
            settings={data.payrollSettings}
            activeTaxTable={data.activeTaxTable}
            showEarnings={false}
            afterAllowances={
              <div className="flex items-center gap-2 flex-wrap rounded-xl border border-[var(--accent-link,#2d5295)]/20 bg-[var(--accent-link,#2d5295)]/5 px-3 py-2.5 text-sm">
                <Copy size={14} className="text-[var(--accent-link,#2d5295)]" />
                <span className="text-gray-600 dark:text-gray-300">Copy {staffFullName(current).split(' ')[0]}'s saved allowances to</span>
                <select value={applyScope} onChange={(e) => setApplyScope(e.target.value as 'all' | StaffCategory)} className={glassFieldClass('py-1 text-sm')}>
                  <option value="all">all staff</option>
                  {(Object.keys(STAFF_CATEGORY_LABELS) as StaffCategory[]).map((c) => (
                    <option key={c} value={c}>{STAFF_CATEGORY_LABELS[c].toLowerCase()} staff</option>
                  ))}
                </select>
                <button onClick={applyAllowances} className="px-3 py-1.5 rounded-lg text-white text-xs font-medium bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)]">
                  Apply
                </button>
                <span className="text-[11px] text-gray-400">adds/updates by name; existing extras kept</span>
              </div>
            }
          />
        </div>
      )}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal((m) => ({ ...m, show: false }))}
        autoDismissMs={3500}
      />
    </div>
  );
}

/* ================================================================== */
/* In-workspace payslip generator (gratuity + balance recovery)        */
/* ================================================================== */
interface ContractLike { id?: string; startDate: string; endDate?: string; active?: boolean; endOfServiceBenefit?: 'gratuity' | 'severance' | 'none' }

function GeneratePanel({
  staff, profile, deductions, contracts, payslips, gratuityPayouts, leaveAllocation, leave,
  activeTaxTable, settings, year, month, canEdit,
}: {
  staff: HrStaff;
  profile?: HrPayrollProfile;
  deductions: HrDeduction[];
  contracts: ContractLike[];
  payslips: HrPayslip[];
  gratuityPayouts: HrGratuityPayout[];
  leaveAllocation?: HrLeaveAllocation;
  leave: HrLeaveRequest[];
  activeTaxTable?: HrTaxTable;
  settings?: HrPayrollSettings;
  year: number;
  month: number;
  canEdit: boolean;
}) {
  const { policies: leavePolicies } = useLeavePolicies();
  const { currentUser } = useAuth();
  const actor = { id: currentUser?.uid ?? 'unknown', email: currentUser?.email };

  const period = useMemo(() => monthPeriod(year, month), [year, month]);

  // Manual PAYE override (per staff+period). Presence => applied (0 is valid).
  const { override: payeOverride } = usePayeOverride(staff.id, period);
  const [payeEdit, setPayeEdit] = useState(false);
  const [payeAmt, setPayeAmt] = useState('');
  const [payeReason, setPayeReason] = useState('');
  const [payeBusy, setPayeBusy] = useState(false);
  async function applyPayeOverride() {
    const amt = Number(payeAmt);
    if (!Number.isFinite(amt) || amt < 0) return; // allow 0, block negatives/blank
    setPayeBusy(true);
    try {
      await savePayeOverride(staff.id, period, Math.round(amt * 100) / 100, payeReason.trim(), actor);
      setPayeEdit(false);
    } finally { setPayeBusy(false); }
  }
  async function resetPayeOverride() {
    setPayeBusy(true);
    try { await clearPayeOverride(staff.id, period, actor); setPayeEdit(false); }
    finally { setPayeBusy(false); }
  }
  const reference = `SLIP/${year}-${String(month).padStart(2, '0')}/${staff.employeeNo}`;
  const basic = profile?.basicSalary ?? 0;
  const gratuityRate = profile?.gratuityRatePct ?? 0;
  const noProfile = !profile || (profile.basicSalary ?? 0) === 0;

  const activeContract = useMemo(() => contracts.find((x) => x.active) ?? contracts[0], [contracts]);
  // Gratuity is paid only when the contract's end-of-service benefit resolves to
  // gratuity (explicit field wins; legacy contracts fall back to the flag).
  const resolvedBenefit = resolveExitBenefit(activeContract?.endOfServiceBenefit, profile?.gratuityEligible ?? false);
  const paysGratuity = (profile?.gratuityEligible ?? false) && resolvedBenefit === 'gratuity';
  const isFinalContractMonth = useMemo(() => {
    if (!activeContract?.endDate) return false;
    const e = new Date(activeContract.endDate);
    return e.getFullYear() === year && e.getMonth() + 1 === month;
  }, [activeContract, year, month]);
  const contractEndLabel = activeContract?.endDate
    ? new Date(activeContract.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  const contractYears = useMemo(() => {
    const c = activeContract;
    if (!c?.startDate) return 2;
    const start = new Date(c.startDate).getTime();
    const end = (c.endDate ? new Date(c.endDate) : new Date()).getTime();
    return Math.max(1, Math.round((end - start) / (365.25 * 24 * 3600 * 1000)));
  }, [activeContract]);

  const [open, setOpen] = useState(false);
  const [isFinal, setIsFinal] = useState(false);
  const [partialPayout, setPartialPayout] = useState(false);
  const [recoverBalances, setRecoverBalances] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<{ slip: AssembledPayslip; recovery: number } | null>(null);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  // On the final month of an eligible contract, open the panel and switch the gratuity on.
  useEffect(() => {
    if (isFinalContractMonth && paysGratuity) { setIsFinal(true); setOpen(true); }
  }, [isFinalContractMonth, paysGratuity]);

  const gratuityActive = (isFinal || partialPayout) && paysGratuity;
  const gratuityBasePaid = useMemo(
    () => contractGratuityBasePaid({
      openingBasicPaid: profile?.openingBasicPaidThisContract,
      contractStartISO: activeContract?.startDate,
      savedPayslips: payslips,
      currentPeriod: period,
      currentBasic: basic,
      payouts: gratuityPayouts.filter((p) => p.payslipId !== payslipId(staff.id, period) && (!activeContract?.id || p.contractId === activeContract.id)),
    }),
    [profile?.openingBasicPaidThisContract, activeContract, payslips, period, basic, gratuityPayouts],
  );
  const suggestedGross = gratuityAccruedOn(gratuityBasePaid, gratuityRate);
  const [grossGratuity, setGrossGratuity] = useState(String(suggestedGross));
  useEffect(() => { setGrossGratuity(String(suggestedGross)); }, [suggestedGross]);

  const gratuity = useMemo(() => {
    if (!gratuityActive || !activeTaxTable) return undefined;
    return computeGratuityTax({
      grossGratuity: Number(grossGratuity) || 0,
      contractYears,
      currentAnnualSalary: basic * 12,
      table: activeTaxTable,
    });
  }, [gratuityActive, profile, activeTaxTable, grossGratuity, contractYears, basic]);

  // Outstanding loan balances still owed AFTER this month's normal instalment.
  const settlements = useMemo(() => {
    if (!isFinal) return [] as { d: HrDeduction; remaining: number }[];
    return deductions
      .filter((d) => d.status === 'active' && DEDUCTION_TYPES[d.type]?.balanceKey)
      .map((d) => ({ d, remaining: deductionForPeriod(d, period).remainingAfter ?? 0 }))
      .filter((x) => x.remaining > 0.005);
  }, [isFinal, deductions, period, settings]);

  const recoveries = useMemo(() => {
    if (!recoverBalances) return undefined;
    const rs = settlements.map(({ d, remaining }) => ({
      code: 'RECOVERY',
      label: `${d.label || DEDUCTION_TYPES[d.type]?.label || 'Loan'} — final settlement`,
      amount: remaining,
      remainingBalance: 0,
    }));
    return rs.length ? rs : undefined;
  }, [recoverBalances, settlements]);

  const assembled: AssembledPayslip = useMemo(
    () => assemblePayslip({
      staff, profile, deductions, taxTable: activeTaxTable,
      leaveAllocation, leaveRequests: leave, period, reference, gratuity, recoveries,
      // Same leave source as the leave module (policies resolve the cohort
      // entitlement); without them the payslip disagrees with the leave screen.
      leavePolicies, leaveAccrualStart: settings?.leaveAccrualStart,
      sickBalancesFromMonth: settings?.sickBalancesFromMonth,
      payeOverride: payeOverride ? payeOverride.amount : undefined,
      payeOverrideReason: payeOverride?.reason,
    }),
    [staff, profile, deductions, activeTaxTable, leaveAllocation, leave, period, reference, gratuity, recoveries, settings, payeOverride, leavePolicies],
  );

  const id = payslipId(staff.id, period);
  const existing = payslips.find((p) => p.id === id);

  // After a save, freeze the panel to exactly what was written so the
  // figures don't silently recompute (settled loans drop out otherwise).
  const frozen = saved !== null;
  const view = saved ? saved.slip : assembled;

  async function download() {
    const logo = await loadLogoDataUrl();
    const pdf = buildPayslipPdf(view, { logoDataUrl: logo, disclaimer: settings?.payslipDisclaimer });
    pdf.save(`payslip_${staff.employeeNo}_${year}-${String(month).padStart(2, '0')}.pdf`);
  }

  async function generateAndSave() {
    setBusy(true);
    setSaveModal({ show: true, status: 'saving', message: 'Rendering & saving payslip…' });
    try {
      const logo = await loadLogoDataUrl();
      const pdf = buildPayslipPdf(assembled, { logoDataUrl: logo, disclaimer: settings?.payslipDisclaimer });
      const blob = pdf.output('blob');
      const path = `payslips/${staff.id}/${id}.pdf`;
      const sref = ref(storage, path);
      await uploadBytes(sref, blob, { contentType: 'application/pdf' });
      const url = await getDownloadURL(sref);
      const nowIso = new Date().toISOString();
      await hr.payslips.set(id, {
        ...assembled, basic, status: 'finalised', pdfUrl: url, storagePath: path,
        finalisedAt: nowIso, finalisedBy: actor.id,
      });
      await recordHrAudit({
        action: 'payslip.generate', actor, subjectId: id,
        after: { period: periodLabel(period), net: assembled.net, taxTable: assembled.taxTableId, paye: assembled.paye, payeOverridden: !!assembled.payeOverridden },
      });
      // Gratuity payout memory — one record per payslip (deduped by id), so
      // its consumed base nets out of the running base and never double-pays.
      if (gratuity) {
        const basePaidOut = gratuityRate > 0 ? round2((Number(grossGratuity) || 0) / (gratuityRate / 100)) : gratuityBasePaid;
        await hr.gratuityPayout.set(id, {
          staffId: staff.id,
          ...(activeContract?.id ? { contractId: activeContract.id } : {}),
          payslipId: id,
          result: gratuity,
          contractYears,
          basePaidOut,
        });
      } else if (gratuityPayouts.some((p) => p.id === id)) {
        await hr.gratuityPayout.delete(id);
      }
      const recovered = (recoveries ?? []).reduce((s, r) => s + r.amount, 0);
      setSaved({ slip: assembled, recovery: recovered });
      setSaveModal({
        show: true, status: 'success',
        message: `Payslip for ${periodLabel(period)} saved${recovered > 0 ? ` — ${fmtPula(recovered)} recovered` : ''}.`,
      });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  const recoveryTotal = settlements.reduce((s, x) => s + x.remaining, 0);

  return (
    <SoftCard hover={false} pad="p-0">
      {/* header / toggle */}
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left">
        <span className="flex items-center gap-2 font-semibold text-gray-800 dark:text-gray-100">
          <Receipt size={16} /> {paysGratuity ? 'Payslip, PAYE & gratuity payout' : resolvedBenefit === 'severance' ? 'Payslip, PAYE & severance payout' : 'Payslip & PAYE'}
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">· {MONTH_LABELS[month - 1]} {year}</span>
          {isFinalContractMonth && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-0.5 text-[11px] font-semibold">
              <Award size={11} /> Final month
            </span>
          )}
        </span>
        <ChevronDown size={18} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-4 space-y-4">
          {saved && (
            <div className="flex items-start gap-3 rounded-xl border border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-900/25 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-300 shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-semibold text-emerald-800 dark:text-emerald-200">
                  Payslip for {periodLabel(saved.slip.period)} saved — net {fmtPula(saved.slip.net)}.
                </p>
                <p className="text-emerald-700 dark:text-emerald-300 mt-0.5">
                  {saved.slip.gratuity && 'Gratuity payout included. '}
                  {saved.recovery > 0
                    ? `${fmtPula(saved.recovery)} of outstanding balances was recovered in full on this payslip. This is exactly what was saved — start another to recalculate live.`
                    : 'This is exactly what was saved.'}
                </p>
              </div>
              <button onClick={() => setSaved(null)} className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline shrink-0">Prepare another</button>
            </div>
          )}
          {noProfile && (
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
              <AlertTriangle size={15} /> No basic salary set — set it in the payroll editor below first.
            </div>
          )}
          {!activeTaxTable && (
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
              <AlertTriangle size={15} /> No active tax table — PAYE will show as 0 until an admin activates one.
            </div>
          )}

          {isFinalContractMonth && (
            <div className="flex items-start gap-3 rounded-xl border-2 border-amber-400 bg-amber-50 dark:border-amber-500/60 dark:bg-amber-900/30 px-4 py-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-amber-800 dark:text-amber-200">
                  Final month of contract — {staffFullName(staff)}&apos;s contract ends {contractEndLabel}.
                </p>
                <p className="text-amber-700 dark:text-amber-300 mt-0.5">
                  {paysGratuity
                    ? 'The gratuity payout is opened below. Review it — gratuity is paid at contract end and resets afterwards.'
                    : 'This contract does not pay gratuity — confirm final dues and any leave payout before saving.'}
                </p>
              </div>
            </div>
          )}

          {/* gratuity toggle + inputs */}
          {!frozen && paysGratuity && (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
              <input type="checkbox" checked={isFinal} onChange={(e) => setIsFinal(e.target.checked)} className="rounded" />
              Final payslip — include gratuity payout
            </label>
          )}
          {!frozen && paysGratuity && !isFinal && (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
              <input type="checkbox" checked={partialPayout} onChange={(e) => setPartialPayout(e.target.checked)} className="rounded" />
              Pay out gratuity this month (partial — loans keep running)
            </label>
          )}
          {!frozen && gratuityActive && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Contract years</span>
                <input value={contractYears} disabled className={glassFieldClass('py-2 opacity-70')} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Gross gratuity (BWP)</span>
                <input type="number" value={grossGratuity} onChange={(e) => setGrossGratuity(e.target.value)} className={glassFieldClass('py-2')} />
              </label>
              {gratuity && (
                <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-col justify-end pb-1">
                  <span>Exempt 50%: {fmtPula(gratuity.exemptAmount)}</span>
                  <span>Tax @ {gratuity.marginalRatePct}%: {fmtPula(gratuity.tax)}</span>
                </div>
              )}
            </div>
          )}

          {/* outstanding balance recovery */}
          {!frozen && isFinal && settlements.length > 0 && (
            <div className="rounded-xl border border-red-200 dark:border-red-500/40 bg-red-50/60 dark:bg-red-900/20 px-3 py-2.5">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100 cursor-pointer">
                <input type="checkbox" checked={recoverBalances} onChange={(e) => setRecoverBalances(e.target.checked)} className="rounded" />
                Recover outstanding balances on this final payslip ({fmtPula(recoveryTotal)})
              </label>
              <ul className="mt-1.5 ml-6 text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                {settlements.map(({ d, remaining }) => (
                  <li key={d.id} className="flex justify-between max-w-xs">
                    <span>{d.label || DEDUCTION_TYPES[d.type]?.label}</span>
                    <span className="font-medium text-red-600 dark:text-red-400">{fmtPula(remaining)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 ml-6 text-[11px] text-gray-500 dark:text-gray-400">
                Recovered in full on this payslip. Generating won&apos;t change the loan — set it to “Paid off” in the editor below to clear it from records.
              </p>
            </div>
          )}

          {/* preview (frozen to the saved slip after a save) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip tone="blue" value={fmtPula(view.earningsTotal)} label="Gross" />
            <StatChip tone="gold" value={fmtPula(view.paye)} label="PAYE" />
            <StatChip tone="red" value={fmtPula(view.deductionsTotal)} label="Deductions" />
            <StatChip tone={view.net < 0 ? 'red' : 'emerald'} value={fmtPula(view.net)} label="Net pay" />
          </div>
          {!frozen && canEdit && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-200">PAYE</span>
                  {assembled.payeOverridden ? (
                    <>
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-0.5 text-[11px] font-semibold">Manual override</span>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        table value {fmtPula(assembled.computedPaye ?? 0)}{payeOverride?.reason ? ` · ${payeOverride.reason}` : ''}
                      </span>
                    </>
                  ) : (
                    <span className="ml-2 text-xs text-gray-400">computed from the tax table</span>
                  )}
                </div>
                {!payeEdit && (
                  <button
                    onClick={() => { setPayeAmt(String(payeOverride ? payeOverride.amount : (assembled.computedPaye ?? 0))); setPayeReason(payeOverride?.reason ?? ''); setPayeEdit(true); }}
                    className="text-xs font-medium text-[#2d5295] hover:underline"
                  >
                    {assembled.payeOverridden ? 'Edit override' : 'Override PAYE'}
                  </button>
                )}
              </div>
              {payeEdit && (
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <label className="block">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 block">Manual PAYE (BWP)</span>
                    <input type="number" min={0} step="0.01" value={payeAmt} onChange={(e) => setPayeAmt(e.target.value)} className={glassFieldClass('py-1.5 text-sm w-32')} />
                  </label>
                  <label className="block flex-1 min-w-[180px]">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 block">Reason (optional)</span>
                    <input value={payeReason} onChange={(e) => setPayeReason(e.target.value)} placeholder="e.g. joined May — part-year, computed separately" className={glassFieldClass('py-1.5 text-sm')} />
                  </label>
                  <button onClick={applyPayeOverride} disabled={payeBusy} className="px-3 py-1.5 text-xs font-medium text-white bg-[#1b3a6b] hover:bg-[#142c52] rounded-lg disabled:opacity-50">Apply</button>
                  {assembled.payeOverridden && (
                    <button onClick={resetPayeOverride} disabled={payeBusy} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 rounded-lg disabled:opacity-50">Reset to computed</button>
                  )}
                  <button onClick={() => setPayeEdit(false)} className="px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:underline">Cancel</button>
                </div>
              )}
              <p className="mt-1.5 text-[11px] text-gray-400">Calculate the correct PAYE separately and enter it here (0 is allowed). Net recalculates automatically. Every change is recorded in the audit log.</p>
            </div>
          )}

          {view.net < 0 && (
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs">
              <AlertTriangle size={14} /> Net is negative — recovery exceeds the final pay. Reduce the recovery or settle the balance separately.
            </div>
          )}

          <div className="rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/50 text-sm">
            {view.lines.map((l, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5">
                <span className={l.kind === 'deduction' ? 'text-gray-600 dark:text-gray-300' : 'text-gray-800 dark:text-gray-100'}>
                  {l.kind === 'deduction' ? '− ' : ''}{l.label}
                  {typeof l.remainingBalance === 'number' && l.remainingBalance > 0 && (
                    <span className="text-xs text-gray-400 ml-1">(bal {fmtPula(l.remainingBalance)})</span>
                  )}
                </span>
                <span className={l.kind === 'deduction' ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100 font-medium'}>
                  {fmtPula(l.amount)}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-end gap-2">
            {!frozen && existing && (
              <span className="text-xs text-amber-600 dark:text-amber-300 mr-auto">
                A payslip for {periodLabel(period)} exists — saving replaces it.
              </span>
            )}
            <button onClick={download} disabled={noProfile} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50">
              <Download size={15} /> Download PDF
            </button>
            {frozen ? (
              <AccentButton icon={Receipt} onClick={() => setSaved(null)}>
                Prepare another
              </AccentButton>
            ) : canEdit && (
              <AccentButton icon={busy ? Loader2 : CheckCircle2} disabled={busy || noProfile} onClick={generateAndSave}>
                {busy ? 'Saving…' : 'Generate & save'}
              </AccentButton>
            )}
          </div>
        </div>
      )}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal((m) => ({ ...m, show: false }))}
      />
    </SoftCard>
  );
}
